import { ReactNode } from "react";
import { V2PageHeader, V2Accent } from "@/components/v2/V2PageHeader";

interface MenuHeadingProps {
  /** Small uppercase eyebrow pill above the title (e.g. "Friends"). */
  eyebrow: string;
  /** The non-accented lead of the headline (dark in light, light in dark). */
  title: string;
  /**
   * The trailing words rendered in the Nexus green gradient — the "in sync."
   * of "Your trade circle, in sync." Omit for a heading with no green accent.
   */
  greenSuffix?: string;
  /** Optional supporting line under the title. */
  subtitle?: ReactNode;
  /** Optional trailing slot (chips/buttons) under the hero. */
  trailing?: ReactNode;
  /**
   * Override the title font-size clamp for longer headlines that would
   * otherwise wrap on narrow phones (b18g). Defaults to the standard clamp.
   */
  titleClamp?: string;
  className?: string;
  "data-testid"?: string;
  /**
   * b18a item 3: opt this heading out of DotRail's per-block spine dot. The
   * page header already carries its own eyebrow dot, so DotRail skips it. The
   * prop is only read off the element by DotRail; it is not rendered to the DOM.
   */
  "data-dotrail-skip"?: boolean;
}

/**
 * b16 item 8: the single canonical menu heading for every top-level page.
 *
 * Extracted from the Friends hero (the spec Sam signed off on): dark display
 * title with the last few words in the Nexus green gradient, a matching
 * eyebrow pill, and an optional subtitle. It delegates rendering to the
 * existing V2PageHeader so all menus share one heading implementation and
 * pick up its correct light/dark behaviour automatically — `title` is drawn
 * in the adaptive ink colour and `greenSuffix` in the V2Accent gradient.
 */
export function MenuHeading({
  eyebrow,
  title,
  greenSuffix,
  subtitle,
  trailing,
  titleClamp,
  className,
  "data-testid": _testid,
}: MenuHeadingProps) {
  return (
    <V2PageHeader
      eyebrow={eyebrow}
      titleClamp={titleClamp}
      title={
        greenSuffix ? (
          <>
            {title} <V2Accent>{greenSuffix}</V2Accent>
          </>
        ) : (
          title
        )
      }
      subtitle={subtitle}
      trailing={trailing}
      className={className}
    />
  );
}
